/**
 * Pure transform functions for converting backend statistics data
 * into the shapes that UI components expect.
 *
 * No classes, no singletons, no side effects.
 */

import type {
  MatchSummary,
  PlayerStatistics,
  TeamInfo,
  RoleInfo,
  CourtZone,
  HeatmapData,
  SegmentStatistics,
  ShotBreakdown,
  ShotBreakdownPlayer,
  ShotBreakdownTeam,
  ShotsTrackingPerformance,
  TeamFormationEntry,
  PlayerSlot,
  PlayersCorrespondance,
} from '@/types/statistics';
import { SHOW_WINNERS } from './constants';
import {
  SHOT_TYPE_CATALOG,
  selectShotConsistency,
  type ShotConsistency,
} from '@/components/tournament/shotTypes';

// ---------------------------------------------------------------------------
// Shared tiny helpers
// ---------------------------------------------------------------------------

/** Round to 1 decimal place */
function r1(v: number): number {
  return Math.round(v * 10) / 10;
}

/** Convert a 0-1 ratio to 0-100 percentage, 1-decimal precision */
function pct1(ratio: number): number {
  return Math.round(ratio * 1000) / 10;
}

/** Convert a 0-1 ratio to a rounded integer percentage (0-100) */
function pct(ratio: number): number {
  return Math.round(ratio * 100);
}

/** Prepend data-URI prefix to a base64 player crop image, or pass through URLs */
export function playerCropToDataUri(base64: string): string {
  if (base64.startsWith('data:') || base64.startsWith('http')) return base64;
  return `data:image/jpeg;base64,${base64}`;
}

/**
 * Per-slot display image map for a block: each player's base64 crop converted
 * to a data URI, with an avatar override substituted when the correspondence
 * map ties that block's slot to a known user UUID. Mirrors the logic that lived
 * in BlockReport. Pure.
 */
export function buildPlayerCrops(
  segment: SegmentStatistics,
  avatarMap?: Record<string, string>,
  playersCorrespondance?: PlayersCorrespondance,
): Record<string, string> {
  const blockId = segment.metadata.block_id;
  const out: Record<string, string> = {};
  for (const [pid, b64] of Object.entries(segment.player_crops)) {
    let avatarOverride: string | null = null;
    if (avatarMap && playersCorrespondance) {
      for (const [uuid, byBlock] of Object.entries(playersCorrespondance)) {
        if (byBlock[blockId] === pid) {
          avatarOverride = avatarMap[uuid] ?? null;
          break;
        }
      }
    }
    out[pid] = avatarOverride ?? playerCropToDataUri(b64);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Match Summary
// ---------------------------------------------------------------------------

export interface MatchSummaryViewData {
  timeInPlay: number; // minutes
  totalGameTime: number; // minutes
  averageRally: number; // seconds, 1 decimal
  longestRally: number; // seconds, 1 decimal
  totalRallies: number;
  rallyTimePercentage: number; // 0-100, 1 decimal
  totalShots: number;
  averageShotsPerRally: number; // 1 decimal
}

export function transformMatchSummary(ms: MatchSummary): MatchSummaryViewData {
  return {
    timeInPlay: Math.round(ms.total_rally_time / 60),
    totalGameTime: Math.round(ms.total_game_time / 60),
    averageRally: r1(ms.average_rally_duration),
    longestRally: r1(ms.longest_rally?.duration ?? 0),
    totalRallies: ms.number_rallies,
    rallyTimePercentage: pct1(ms.rally_time_percentage),
    totalShots: ms.total_shots,
    averageShotsPerRally: r1(ms.average_shots_per_rally),
  };
}

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

export interface ResolvedTeams {
  myTeam: string[];
  opponentTeam: string[];
}

/**
 * Given the segment `teams` record and the identified player, figure out
 * which team is "mine" and which is the opponent's.
 */
export function resolveTeams(
  teams: Record<string, TeamInfo>,
  identifiedPlayerId: string,
  roles?: Record<string, RoleInfo>,
): ResolvedTeams {
  const entries = Object.entries(teams);
  const myEntry = entries.find(([, t]) =>
    t.teammates.includes(identifiedPlayerId),
  );
  const opponentEntry = entries.find(([k]) => k !== myEntry?.[0]);

  const sortByRole = (players: string[]) => {
    if (!roles) return players;
    return [...players].sort((a, b) => {
      const roleA = roles[a]?.primary_role;
      const roleB = roles[b]?.primary_role;
      if (roleA === 'left' && roleB !== 'left') return -1;
      if (roleB === 'left' && roleA !== 'left') return 1;
      return 0;
    });
  };

  return {
    myTeam: sortByRole(myEntry?.[1].teammates ?? []),
    opponentTeam: sortByRole(opponentEntry?.[1].teammates ?? []),
  };
}

/**
 * Return the teammate ID(s) for a given player. Usually there's exactly one.
 */
export function getTeammates(
  teams: Record<string, TeamInfo>,
  playerId: string,
): string[] {
  for (const team of Object.values(teams)) {
    if (team.teammates.includes(playerId)) {
      return team.teammates.filter((id) => id !== playerId);
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Speed Ranking
// ---------------------------------------------------------------------------

export interface SpeedEntry {
  playerId: string;
  averageSpeed: number; // km/h, 1 decimal
  playerCrop: string; // data URI
  isMe: boolean;
  isTeammate: boolean;
}

/**
 * Build a sorted-by-average-speed-desc list of all players in a segment.
 */
export function getSpeedRanking(
  playersStats: Record<string, PlayerStatistics>,
  playerCrops: Record<string, string>,
  identifiedPlayerId: string,
  teammates: string[],
): SpeedEntry[] {
  return Object.entries(playersStats)
    .map(([pid, stats]) => ({
      playerId: pid,
      averageSpeed: r1(stats.mean_velocity),
      playerCrop: playerCrops[pid] ? playerCropToDataUri(playerCrops[pid]) : '',
      isMe: pid === identifiedPlayerId,
      isTeammate: teammates.includes(pid),
    }))
    .sort((a, b) => b.averageSpeed - a.averageSpeed);
}

// ---------------------------------------------------------------------------
// Distance Ranking
// ---------------------------------------------------------------------------

export interface DistanceEntry {
  playerId: string;
  runningDistance: number; // meters, rounded int
  playerCrop: string; // data URI
  isMe: boolean;
  isTeammate: boolean;
}

/**
 * Build a sorted-by-distance-desc list of all players in a segment.
 */
export function getDistanceRanking(
  playersStats: Record<string, PlayerStatistics>,
  playerCrops: Record<string, string>,
  identifiedPlayerId: string,
  teammates: string[],
): DistanceEntry[] {
  return Object.entries(playersStats)
    .map(([pid, stats]) => ({
      playerId: pid,
      runningDistance: Math.round(stats.running_distance),
      playerCrop: playerCrops[pid] ? playerCropToDataUri(playerCrops[pid]) : '',
      isMe: pid === identifiedPlayerId,
      isTeammate: teammates.includes(pid),
    }))
    .sort((a, b) => b.runningDistance - a.runningDistance);
}

// ---------------------------------------------------------------------------
// Shot Analysis (from shots_breakdown)
// ---------------------------------------------------------------------------

export interface ShotHit {
  name: string;
  percentage: number; // 0-100, 1 decimal
}

export interface ShotAnalysisPlayerData {
  playerId: string;
  playerCrop: string; // data URI
  offensive: number; // 0-100, 1 decimal
  defensive: number; // 0-100, 1 decimal
  offensiveHits: ShotHit[];
  defensiveHits: ShotHit[];
}

const shotAnalysisKeys = (group: 'offensive' | 'defensive') =>
  SHOT_TYPE_CATALOG.filter((shotType) => shotType.group === group).map(
    (shotType) => ({
      key: shotType.key,
      backendKeys: shotType.backendKeys,
      labelKey: `report:shot.${
        shotType.key === 'forehand_volley'
          ? 'forehandVolley'
          : shotType.key === 'backhand_volley'
            ? 'backhandVolley'
            : shotType.key === 'forehand_lob'
              ? 'forehandLob'
              : shotType.key === 'backhand_lob'
                ? 'backhandLob'
                : shotType.key
      }`,
    }),
  );

const OFFENSIVE_KEYS = shotAnalysisKeys('offensive');
const DEFENSIVE_KEYS = shotAnalysisKeys('defensive');

/**
 * Extract shot analysis data for all players from the segment's
 * shots_breakdown field.
 *
 * shots_breakdown is `Record<string, unknown> | null`. When null, returns
 * an empty array — the component should handle that gracefully.
 */
export function transformShotAnalysis(
  shotsBreakdown: ShotBreakdown | null,
  playerCrops: Record<string, string>,
  allPlayerIds: string[],
  _identifiedPlayerId: string,
  _teammates: string[],
): ShotAnalysisPlayerData[] {
  if (!shotsBreakdown) return [];

  const { players } = shotsBreakdown;
  if (!players) return [];

  return allPlayerIds.map((pid) => {
    const playerData = players[pid] as ShotBreakdownPlayer | undefined;
    const summary = playerData?.summary;
    const offensive = playerData?.offensive_shots_ratio ?? {};
    const defensive = playerData?.defensive_shots_ratio ?? {};

    return {
      playerId: pid,
      playerCrop: playerCrops[pid] ? playerCropToDataUri(playerCrops[pid]) : '',
      offensive: pct1(summary?.offensive_total_ratio ?? 0),
      defensive: pct1(summary?.defensive_total_ratio ?? 0),
      offensiveHits: OFFENSIVE_KEYS.map(({ backendKeys, labelKey }) => ({
        name: labelKey,
        percentage: pct1(
          backendKeys.reduce((sum, key) => sum + (offensive[key] ?? 0), 0),
        ),
      })),
      defensiveHits: DEFENSIVE_KEYS.map(({ backendKeys, labelKey }) => ({
        name: labelKey,
        percentage: pct1(
          backendKeys.reduce((sum, key) => sum + (defensive[key] ?? 0), 0),
        ),
      })),
    };
  });
}

// ---------------------------------------------------------------------------
// Shot Analysis — Team level
// ---------------------------------------------------------------------------

export interface ShotShareEntry {
  playerId: string;
  percentage: number; // 0-100, 1 decimal
  count: number; // raw shot count
}

export interface ShotAnalysisTeamData {
  teamId: string;
  teammates: string[];
  offensive: number; // 0-100, 1 decimal
  defensive: number; // 0-100, 1 decimal
  offensiveHits: ShotHit[];
  defensiveHits: ShotHit[];
  shotShare: Record<string, ShotShareEntry[]>; // per shot type, sorted (identified player first)
}

/** Look up the raw shot count for a player + shot type. */
function getPlayerShotCount(
  playerData: ShotBreakdownPlayer | undefined,
  shotType: string,
): number {
  if (!playerData) return 0;
  switch (shotType) {
    case 'total':
      return playerData.summary.total_shots ?? 0;
    case 'forehand':
      return playerData.defensive_shots?.forehand ?? 0;
    case 'backhand':
      return playerData.defensive_shots?.backhand ?? 0;
    case 'forehand_lob':
      return playerData.defensive_shots?.forehand_lob ?? 0;
    case 'backhand_lob':
      return playerData.defensive_shots?.backhand_lob ?? 0;
    case 'forehand_volley':
      return playerData.offensive_shots?.forehand_volley ?? 0;
    case 'backhand_volley':
      return playerData.offensive_shots?.backhand_volley ?? 0;
    case 'overhead':
      return playerData.offensive_shots?.overhead ?? 0;
    case 'serve':
      return playerData.other_shots?.serve ?? 0;
    default:
      return 0;
  }
}

/** Convert raw shot_share into pre-sorted, display-ready entries with counts. */
function transformShotShare(
  raw: Record<string, Record<string, number>> | undefined,
  playersData: Record<string, unknown>,
  identifiedPlayerId: string,
): Record<string, ShotShareEntry[]> {
  if (!raw) return {};
  const result: Record<string, ShotShareEntry[]> = {};
  for (const [shotType, players] of Object.entries(raw)) {
    result[shotType] = Object.entries(players)
      .map(([playerId, ratio]) => ({
        playerId,
        percentage: pct1(ratio),
        count: getPlayerShotCount(
          playersData[playerId] as ShotBreakdownPlayer | undefined,
          shotType,
        ),
      }))
      .sort((a, b) => {
        if (a.playerId === identifiedPlayerId) return -1;
        if (b.playerId === identifiedPlayerId) return 1;
        return 0;
      });
  }
  return result;
}

/**
 * Extract shot analysis data for all teams from the segment's
 * shots_breakdown field. Returns identified player's team first.
 */
export function transformShotAnalysisTeams(
  shotsBreakdown: ShotBreakdown | null,
  identifiedPlayerId: string,
): ShotAnalysisTeamData[] {
  if (!shotsBreakdown) return [];

  const { teams } = shotsBreakdown;
  if (!teams) return [];

  const result: ShotAnalysisTeamData[] = [];

  for (const [teamId, teamData] of Object.entries(teams)) {
    // Skip meta-keys like most_offensive_team (strings, not objects)
    if (typeof teamData === 'string') continue;

    const team = teamData as ShotBreakdownTeam;
    if (!team.teammates) continue;

    const offensive = team.offensive_shots_ratio ?? {};
    const defensive = team.defensive_shots_ratio ?? {};

    result.push({
      teamId,
      teammates: team.teammates,
      offensive: pct1(team.offensive_total_ratio ?? 0),
      defensive: pct1(team.defensive_total_ratio ?? 0),
      offensiveHits: OFFENSIVE_KEYS.map(({ backendKeys, labelKey }) => ({
        name: labelKey,
        percentage: pct1(
          backendKeys.reduce((sum, key) => sum + (offensive[key] ?? 0), 0),
        ),
      })),
      defensiveHits: DEFENSIVE_KEYS.map(({ backendKeys, labelKey }) => ({
        name: labelKey,
        percentage: pct1(
          backendKeys.reduce((sum, key) => sum + (defensive[key] ?? 0), 0),
        ),
      })),
      shotShare: transformShotShare(
        team.shot_share,
        shotsBreakdown.players,
        identifiedPlayerId,
      ),
    });
  }

  // Sort so identified player's team comes first
  result.sort((a, b) => {
    const aHas = a.teammates.includes(identifiedPlayerId) ? 0 : 1;
    const bHas = b.teammates.includes(identifiedPlayerId) ? 0 : 1;
    return aHas - bHas;
  });

  return result;
}

// ---------------------------------------------------------------------------
// Player Awards (from shots_breakdown + shots_tracking_performance)
// ---------------------------------------------------------------------------

// "Most defensive" is deliberately absent: the backend computes it
// (`most_defensive_player`), but playing the most defensive shots describes a
// style, it doesn't win anything — on a podium it reads as a consolation
// prize. Every category here is something a player would want to be told.
export interface PlayerAward {
  category:
    | 'mostOffensive'
    | 'mostConsistent'
    | 'mostDecisive'
    | 'hardestHitter';
  playerId: string;
  playerCrop: string;
  isMe: boolean;
  isTeammate: boolean;
  statValue: string; // pre-formatted, e.g. "53.2%"
  /** Unit to set beside the value at a smaller size. Percentages keep their
      sign inside `statValue`; a real unit like km/h is carried here so the
      card can render it as a suffix rather than in the hero face. */
  statUnit?: string;
}

/**
 * Build the player awards from pre-computed backend fields. Hardest Hitter is
 * sourced independently from the sibling offensive-ball-speed aggregate, so a
 * payload with speeds but no breakdown still yields that one.
 *
 * Most Consistent and Most Decisive are the exception: both the winner and the
 * percentage are recomputed from `playersStatistics`, over the same accepted
 * rally shots the consistency card and the ball map count. The backend's
 * `performance` block pools the serve aliases into its rates, so reading it
 * here would print a different number to the card two sections below — and,
 * in a close field, could hand the badge to a different player.
 */
export function transformPlayerAwards(
  shotsBreakdown: ShotBreakdown | null,
  shotsTrackingPerformance: ShotsTrackingPerformance | null | undefined,
  playerCrops: Record<string, string>,
  identifiedPlayerId: string,
  teammates: string[],
  playersStatistics: Record<string, PlayerStatistics>,
): PlayerAward[] | null {
  const awards: PlayerAward[] = [];
  const players = shotsBreakdown?.players;
  const performance = shotsTrackingPerformance?.performance;
  const offensivePid = players?.most_offensive_player;

  // Fewest errors and most winners, as a share of each player's own rally
  // shots. Ties keep the first player encountered — stable, and the podium
  // shows one name.
  const rates = Object.entries(playersStatistics).flatMap(([pid, ps]) => {
    const r = selectShotConsistency(ps, 'all');
    return r ? [[pid, r] as const] : [];
  });
  const bestBy = (
    better: (a: ShotConsistency, b: ShotConsistency) => boolean,
  ) =>
    rates.length
      ? rates.reduce((best, cur) => (better(cur[1], best[1]) ? cur : best))
      : null;
  const consistent = bestBy((a, b) => a.errorRate < b.errorRate);
  const decisive = bestBy((a, b) => a.winnerRate > b.winnerRate);

  const buildAward = (
    category: PlayerAward['category'],
    pid: string,
    statValue: string,
    statUnit?: string,
  ): PlayerAward => ({
    category,
    playerId: pid,
    playerCrop: playerCrops[pid] ?? '',
    isMe: pid === identifiedPlayerId,
    isTeammate: teammates.includes(pid),
    statValue,
    statUnit,
  });

  // Extract supporting stats
  const offensivePlayer =
    offensivePid && players
      ? (players[offensivePid] as ShotBreakdownPlayer | undefined)
      : undefined;
  if (players && performance?.global) {
    awards.push(
      buildAward(
        'mostOffensive',
        offensivePid!,
        `${pct1(offensivePlayer?.summary?.offensive_total_ratio ?? 0)}%`,
      ),
    );
    if (consistent) {
      awards.push(
        buildAward(
          'mostConsistent',
          consistent[0],
          `${pct1(consistent[1].errorRate)}%`,
        ),
      );
    }
    // Built on the backend's `is_winner` flag, so it rides the same switch as
    // the ball map's winner marker.
    if (SHOW_WINNERS && decisive) {
      awards.push(
        buildAward(
          'mostDecisive',
          decisive[0],
          `${pct1(decisive[1].winnerRate)}%`,
        ),
      );
    }
  }

  const ballSpeed = shotsTrackingPerformance?.offensive_ball_speed;
  const hitterId = ballSpeed?.hardest_hitter;
  const hitter = hitterId ? ballSpeed.players[hitterId] : null;
  if (
    hitterId &&
    hitter?.eligible === true &&
    hitter.n_with_speed >= ballSpeed.min_shots_required &&
    hitter.n_with_speed > 0 &&
    hitter.mean_speed_km_h !== null &&
    Number.isFinite(hitter.mean_speed_km_h)
  ) {
    awards.push(
      buildAward(
        'hardestHitter',
        hitterId,
        hitter.mean_speed_km_h.toFixed(1),
        'km/h',
      ),
    );
  }

  return awards.length ? awards : [];
}

// ---------------------------------------------------------------------------
// Court zone transforms (for BallMap)
// ---------------------------------------------------------------------------

export interface CourtZoneViewData {
  region: string;
  value: number; // 0-100 integer
}

/** Canonical zone ordering (9 zones) */
const ZONE_ORDER = [
  'net_left',
  'net_middle',
  'net_right',
  'transition_left',
  'transition_middle',
  'transition_right',
  'back_left',
  'back_middle',
  'back_right',
];

export function transformCourtZones(cz: CourtZone): CourtZoneViewData[] {
  return ZONE_ORDER.map((zone) => ({
    region: zone,
    value: pct(cz.zones[zone] ?? 0),
  }));
}

export function transformCourtSides(cz: CourtZone): CourtZoneViewData[] {
  return [
    { region: 'left', value: pct(cz.sides.left) },
    { region: 'middle', value: pct(cz.sides.middle) },
    { region: 'right', value: pct(cz.sides.right) },
  ];
}

export function transformFrontBack(cz: CourtZone): CourtZoneViewData[] {
  return [
    { region: 'front', value: pct(cz.front_back.net) },
    { region: 'transition', value: pct(cz.front_back.transition) },
    { region: 'back', value: pct(cz.front_back.back) },
  ];
}

// ---------------------------------------------------------------------------
// Heatmap grid data (for BallMap continuous heatmap)
// ---------------------------------------------------------------------------

export interface HeatmapGridViewData {
  grid: number[][];
  xEdges: number[];
  yEdges: number[];
  totalPoints: number;
  gridSize: number[];
  courtBounds: number[][];
  mirrorNegativeY: boolean;
}

export function transformHeatmapGrid(hm: HeatmapData): HeatmapGridViewData {
  return {
    grid: hm.heatmap,
    xEdges: hm.x_edges,
    yEdges: hm.y_edges,
    totalPoints: hm.total_points,
    gridSize: hm.grid_size,
    courtBounds: hm.court_bounds,
    mirrorNegativeY: hm.mirror_negative_y,
  };
}

// ---------------------------------------------------------------------------
// Coordinate transforms (backend meters → frontend SVG units → pixels)
// Ported from the old coordinateTransformer.ts as pure functions.
// ---------------------------------------------------------------------------

/** Backend court coordinate system (meters) */
const BACKEND = {
  X_MIN: -5,
  X_MAX: 5,
  Y_MIN: 0,
  Y_MAX: 10,
} as const;

/** Frontend SVG coordinate system */
const FRONTEND = {
  X_MIN: 10,
  X_MAX: 90,
  Y_MIN: 0,
  Y_MAX: 75,
} as const;

const BACKEND_WIDTH = BACKEND.X_MAX - BACKEND.X_MIN; // 10
const BACKEND_HEIGHT = BACKEND.Y_MAX - BACKEND.Y_MIN; // 10
const FRONTEND_WIDTH = FRONTEND.X_MAX - FRONTEND.X_MIN; // 80
const FRONTEND_HEIGHT = FRONTEND.Y_MAX - FRONTEND.Y_MIN; // 75

/** Backend X (meters) → Frontend SVG X */
export function backendToFrontendX(backendX: number): number {
  const normalised = (backendX - BACKEND.X_MIN) / BACKEND_WIDTH;
  return FRONTEND.X_MIN + normalised * FRONTEND_WIDTH;
}

/** Backend Y (meters) → Frontend SVG Y, with optional mirroring */
export function backendToFrontendY(
  backendY: number,
  shouldMirror = false,
): number {
  const y = shouldMirror ? BACKEND.Y_MAX - backendY : backendY;
  const normalised = (y - BACKEND.Y_MIN) / BACKEND_HEIGHT;
  return FRONTEND.Y_MIN + normalised * FRONTEND_HEIGHT;
}

/** Transform a shot's backend position into SVG coordinates */
export function transformShotPosition(position: { x: number; y: number }): {
  x: number;
  y: number;
} {
  return {
    x: backendToFrontendX(position.x),
    y: backendToFrontendY(position.y, false),
  };
}

/**
 * Convert a heatmap grid cell to pixel coordinates within a container.
 * Used by the Court component when rendering the continuous heatmap.
 */
export function heatmapGridToPixel(
  gridX: number,
  gridY: number,
  xEdges: number[],
  yEdges: number[],
  shouldMirror: boolean,
  containerWidth: number,
  containerHeight: number,
): { x: number; y: number } {
  // Centre of the grid cell in backend metres, clamped to court bounds
  const bx = Math.max(
    -5,
    Math.min(5, xEdges[gridX] + (xEdges[gridX + 1] - xEdges[gridX]) / 2),
  );
  const by = Math.max(
    0,
    Math.min(10, yEdges[gridY] + (yEdges[gridY + 1] - yEdges[gridY]) / 2),
  );

  // Backend → Frontend SVG units
  const fx = backendToFrontendX(bx);
  const fy = backendToFrontendY(by, shouldMirror);

  // Frontend SVG units → container pixels
  const px = ((fx - FRONTEND.X_MIN) / FRONTEND_WIDTH) * containerWidth;
  const py = ((fy - FRONTEND.Y_MIN) / FRONTEND_HEIGHT) * containerHeight;

  return { x: Math.round(px), y: Math.round(py) };
}

// ---------------------------------------------------------------------------
// BallMap per-player aggregated data (used by BlockReport to build props)
// ---------------------------------------------------------------------------

/** A shot hit on court — matches BallMap/types.Shot shape */
export interface BallMapShot {
  x: number;
  y: number;
  type: string;
  result: string;
  isWinner?: boolean;
}

export interface ShotPerformanceViewData {
  inRate: number | null; // 0-100
  errorRate: number | null; // 0-100
  winnerRate: number | null; // 0-100
}

export interface BallMapPlayerData {
  playerId: string;
  playerCrop: string;
  zones: CourtZoneViewData[];
  sides: CourtZoneViewData[];
  frontBack: CourtZoneViewData[];
  heatmapGrid: HeatmapGridViewData;
  shots: BallMapShot[];
  /** Per-shot-type performance keyed by frontend shot type + 'global' */
  performance: Record<string, ShotPerformanceViewData>;
}

/** Map backend snake_case shot type to frontend camelCase */
const SHOT_TYPE_MAP: Record<string, string> = {
  rally_server: 'serve',
  forehand_volley: 'forehandVolley',
  backhand_volley: 'backhandVolley',
  forehand_lob: 'forehandLob',
  backhand_lob: 'backhandLob',
  // TODO: smash is currently folded into overhead so smash shots appear
  // under the existing Overhead filter on Smart Court. This is a
  // visualization-level merge only — the performance loop below drops
  // smash entirely (see comment there). Replace with a proper merge
  // (shot-count-weighted in_rate / error_rate / winner_rate against
  // overhead) once the strategy is finalized, and expose smash as its
  // own type if/when it gets its own UI treatment.
  smash: 'overhead',
};

/** Map backend outcome type to BallMap result */
function mapOutcomeToResult(
  outcomeType: string | undefined,
  isError?: boolean,
): 'bounce' | 'interception' | 'out' {
  // Use is_error flag when available; fall back to legacy type-based logic
  if (isError !== undefined) {
    if (isError) return 'out';
    if (outcomeType === 'floor_bounce') return 'bounce';
    return 'interception';
  }
  if (outcomeType === 'floor_bounce') return 'bounce';
  if (outcomeType === 'net' || outcomeType === 'wall_bounce') return 'out';
  return 'interception';
}

export function transformBallMapPlayer(
  pid: string,
  stats: PlayerStatistics,
  playerCrops: Record<string, string>,
): BallMapPlayerData {
  const shots: BallMapShot[] = [];

  if (stats.shots_tracking?.shot_outcomes) {
    for (const [shotType, entries] of Object.entries(
      stats.shots_tracking.shot_outcomes,
    )) {
      // Serve detection is unreliable upstream — drop both the spurious
      // 'serve' key and the canonical 'rally_server' from Smart Court so
      // no serve dots render. Other report sections (e.g. ShotAnalysis)
      // still consume serve data; this filter is BallMap-scoped.
      if (shotType === 'serve' || shotType === 'rally_server') continue;

      for (const entry of entries) {
        if (!entry.accepted) continue;
        // Use mirrored landing position (player-relative, Y always [0,10])
        const outcomePos =
          entry.outcome.position_mirroed ?? entry.outcome.position;
        if (!outcomePos || outcomePos.x == null || outcomePos.y == null)
          continue;

        // Clamp to player-relative court bounds: X [-5, 5], Y [0, 10]
        const pos = transformShotPosition({
          x: Math.max(-5, Math.min(5, outcomePos.x)),
          y: Math.max(0, Math.min(10, outcomePos.y)),
        });

        shots.push({
          x: pos.x,
          y: pos.y,
          type: SHOT_TYPE_MAP[shotType] ?? shotType,
          result: mapOutcomeToResult(entry.outcome.type, entry.is_error),
          isWinner: entry.is_winner,
        });
      }
    }
  }

  // Transform performance data: map backend keys to frontend keys
  const performance: Record<string, ShotPerformanceViewData> = {};
  if (stats.shots_tracking?.performance) {
    for (const [backendKey, perf] of Object.entries(
      stats.shots_tracking.performance,
    )) {
      // Match the shot-stream filter: drop both serve aliases so the
      // ball-hits performance bar doesn't show a Serviço bucket.
      if (backendKey === 'serve' || backendKey === 'rally_server') continue;
      // TODO: smash is intentionally dropped here while we figure out how
      // to merge its in_rate / error_rate / winner_rate into overhead's
      // numbers (shot-count-weighted average is the likely answer). For
      // now, the Overhead filter shows overhead's own performance only,
      // even though shot dots include smash hits.
      if (backendKey === 'smash') continue;

      const frontendKey = SHOT_TYPE_MAP[backendKey] ?? backendKey;
      performance[frontendKey] = {
        inRate: perf.in_rate != null ? pct1(perf.in_rate) : null,
        errorRate: perf.error_rate != null ? pct1(perf.error_rate) : null,
        winnerRate: perf.winner_rate != null ? pct1(perf.winner_rate) : null,
      };
    }
  }

  return {
    playerId: pid,
    playerCrop: playerCrops[pid] ? playerCropToDataUri(playerCrops[pid]) : '',
    zones: transformCourtZones(stats.court_zone),
    sides: transformCourtSides(stats.court_zone),
    frontBack: transformFrontBack(stats.court_zone),
    heatmapGrid: transformHeatmapGrid(stats.heatmap),
    shots,
    performance,
  };
}

// ---------------------------------------------------------------------------
// Ordered player list (for PlayerSelector in components with local switching)
// ---------------------------------------------------------------------------

export interface OrderedPlayer {
  id: string;
  position: string;
  playerCrop: string;
}

/**
 * Produce an ordered list of players for the PlayerSelector:
 * [identified, teammate, opponent1, opponent2]
 * Within each group, left-role players come before right-role players.
 */
export function getOrderedPlayers(
  allPlayerIds: string[],
  identifiedPlayerId: string,
  teammates: string[],
  playerCrops: Record<string, string>,
  roles?: Record<string, RoleInfo>,
): OrderedPlayer[] {
  const sortByRole = (players: string[]) => {
    if (!roles) return players;
    return [...players].sort((a, b) => {
      const roleA = roles[a]?.primary_role;
      const roleB = roles[b]?.primary_role;
      if (roleA === 'left' && roleB !== 'left') return -1;
      if (roleB === 'left' && roleA !== 'left') return 1;
      return 0;
    });
  };

  const myTeam = sortByRole([identifiedPlayerId, ...teammates]);
  const opponents = sortByRole(
    allPlayerIds.filter(
      (id) => id !== identifiedPlayerId && !teammates.includes(id),
    ),
  );

  return [...myTeam, ...opponents].map((pid) => ({
    id: pid,
    position: '', // Not used in current UI
    playerCrop: playerCrops[pid] ? playerCropToDataUri(playerCrops[pid]) : '',
  }));
}

// ---------------------------------------------------------------------------
// Formation Analysis (team positioning breakdown)
// ---------------------------------------------------------------------------

export interface FormationBreakdown {
  key: string;
  labelKey: string;
  percentage: number; // 0-100 integer
}

export interface NetDominanceViewData {
  netTimeRatio: number; // 0-100
  netConquestsPerRally: number; // raw, 2 decimal
  avgNetHoldDuration: number; // seconds, 1 decimal
  avgBackToNetDuration: number; // seconds, 1 decimal
}

export interface TeamSyncViewData {
  depthSyncScore: number; // 0-100
  lateralCoverageRatio: number; // 0-100
}

export interface FormationTeamViewData {
  teamId: string;
  teammates: string[];
  formations: FormationBreakdown[];
  netDominance: NetDominanceViewData;
  teamSync: TeamSyncViewData;
}

const FORMATION_KEYS: { key: string; labelKey: string }[] = [
  { key: 'both_net', labelKey: 'report:formation.bothNet' },
  { key: 'both_back', labelKey: 'report:formation.bothBack' },
  { key: 'net_and_back', labelKey: 'report:formation.netAndBack' },
  { key: 'net_and_transition', labelKey: 'report:formation.netAndTransition' },
  {
    key: 'back_and_transition',
    labelKey: 'report:formation.backAndTransition',
  },
  { key: 'both_transition', labelKey: 'report:formation.bothTransition' },
];

/**
 * Transform formation_analysis data into view-ready format.
 * Returns identified player's team first.
 */
export function transformFormationAnalysis(
  formationAnalysis: Record<string, TeamFormationEntry> | null | undefined,
  identifiedPlayerId: string,
): FormationTeamViewData[] {
  if (!formationAnalysis) return [];

  const result: FormationTeamViewData[] = [];

  for (const [teamId, entry] of Object.entries(formationAnalysis)) {
    const fa = entry.formation_analysis;

    const formations: FormationBreakdown[] = FORMATION_KEYS.map(
      ({ key, labelKey }) => ({
        key,
        labelKey,
        percentage: pct((fa as unknown as Record<string, number>)[key] ?? 0),
      }),
    ).sort((a, b) => b.percentage - a.percentage);

    result.push({
      teamId,
      teammates: entry.teammates,
      formations,
      netDominance: {
        netTimeRatio: pct(fa.net_dominance.net_time_ratio),
        netConquestsPerRally:
          Math.round(fa.net_dominance.net_conquests_per_rally * 100) / 100,
        avgNetHoldDuration: r1(fa.net_dominance.avg_net_hold_duration),
        avgBackToNetDuration: r1(fa.net_dominance.avg_back_to_net_duration),
      },
      teamSync: {
        depthSyncScore: pct(fa.team_synchronization.depth_sync_score),
        lateralCoverageRatio: pct(
          fa.team_synchronization.lateral_coverage_ratio,
        ),
      },
    });
  }

  // Sort so identified player's team comes first
  result.sort((a, b) => {
    const aHas = a.teammates.includes(identifiedPlayerId) ? 0 : 1;
    const bHas = b.teammates.includes(identifiedPlayerId) ? 0 : 1;
    return aHas - bHas;
  });

  return result;
}

// ---------------------------------------------------------------------------
// Backend integration helpers
// ---------------------------------------------------------------------------

/**
 * Normalize the backend `players` array into a UUID → avatar URL map.
 * Input shape: `[[["player_id","UUID"],["player_avatar","URL"]],...]`
 */
export function parsePlayersArray(
  players: [string, string][][],
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const playerEntries of players) {
    let playerId: string | null = null;
    let avatarUrl: string | null = null;
    for (const [key, value] of playerEntries) {
      if (key === 'player_id') playerId = value;
      if (key === 'player_avatar') avatarUrl = value;
    }
    if (playerId && avatarUrl && !avatarUrl.endsWith('/default/user.jpg')) {
      result[playerId] = avatarUrl;
    }
  }
  return result;
}

/**
 * Derive pre-identifications from the current user's by-block correspondence.
 * Returns `Record<filteredIndex, slot>` for segments that are already
 * identified. Each segment is looked up by its `metadata.block_id`.
 */
export function computePreIdentifications(
  correspondence: Record<string, PlayerSlot> | undefined,
  filteredSegments: SegmentStatistics[],
): Record<number, PlayerSlot> {
  const result: Record<number, PlayerSlot> = {};
  if (!correspondence) return result;
  filteredSegments.forEach((seg, i) => {
    const slot = correspondence[seg.metadata.block_id];
    if (slot != null) result[i] = slot;
  });
  return result;
}

/**
 * For a given block, find all internal slots claimed by OTHER UUIDs.
 * NOT_PRESENT entries from other users are ignored — that value means
 * "this user isn't in this block", not a claim on any slot.
 */
export function computeDisabledPlayers(
  blockId: string,
  playersCorrespondance: Record<string, Record<string, PlayerSlot>>,
  currentUserUuid: string,
): Set<PlayerSlot> {
  const disabled = new Set<PlayerSlot>();
  for (const [uuid, byBlock] of Object.entries(playersCorrespondance)) {
    if (uuid === currentUserUuid) continue;
    const slot = byBlock[blockId];
    if (slot != null && slot !== 'NOT_PRESENT') disabled.add(slot);
  }
  return disabled;
}

/**
 * Build the PUT payload `{block_id: slot}` map from identifications,
 * keyed by each filtered segment's `metadata.block_id`. Unidentified
 * segments are omitted (upsert semantics — backend leaves them untouched).
 */
export function buildAssociationsMap(
  identifications: Record<number, PlayerSlot>,
  filteredSegments: SegmentStatistics[],
): Record<string, PlayerSlot> {
  const out: Record<string, PlayerSlot> = {};
  filteredSegments.forEach((seg, i) => {
    const slot = identifications[i];
    if (slot != null) out[seg.metadata.block_id] = slot;
  });
  return out;
}
